import React from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  BoxProps,
} from '@mui/material';
import { SearchIcon } from 'components/SVG';
import CloseIcon from '@mui/icons-material/Close';
import { useDebounce } from 'hooks/common';

type SparkSearchFieldProps = {
  placeholder?: string;
  onChange: (query: string) => void;
  value?: string;
  delay?: number;
  minLength?: number;
  sx?: BoxProps['sx'];
  fullWidth?: boolean;
  autoFocus?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
};

function SparkSearchField({
  placeholder = 'Search',
  onChange,
  value: externalValue,
  delay = 300,
  minLength = 3,
  sx,
  fullWidth = true,
  autoFocus = false,
  inputProps,
}: SparkSearchFieldProps) {
  const [localValue, setLocalValue] = React.useState(externalValue || '');
  const debounce = useDebounce();

  const debouncedOnChange = React.useMemo(
    () =>
      debounce((query: string) => {
        if (query.length >= minLength || query.length === 0) {
          onChange(query);
        }
      }, delay),
    [debounce, delay, minLength, onChange]
  );

  React.useEffect(() => {
    if (externalValue !== undefined && externalValue !== localValue) {
      setLocalValue(externalValue);
    }
  }, [externalValue, localValue]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  return (
    <Box sx={sx}>
      <TextField
        value={localValue}
        placeholder={placeholder}
        fullWidth={fullWidth}
        autoComplete='off'
        onChange={handleChange}
        autoFocus={autoFocus}
        inputProps={inputProps}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: localValue.length > 0 && (
            <InputAdornment position='end'>
              <IconButton
                onClick={handleClear}
                aria-label='Clear search'
                size='small'
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #EEEEEE',
          borderRadius: '12px',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
      />
    </Box>
  );
}

export default SparkSearchField;
